/**
 * @file        src/pages/flight/BookingDetailsDrawer.jsx
 * @description Read-only detail view for a flight booking (Booking List → Details).
 *              Renders passengers, per-sector itinerary, itemised fare and contact
 *              from OUR stored booking_details (same source as the customer
 *              voucher), plus deep links to the voucher/invoice + Update/Cancel.
 * @author      Kinetika Labs
 * @created     2026-07-09
 * @changelog
 *   - 2026-10-09 Claude — Full-screen button beside the close X opens these details as
 *                          their own page in a new tab (`page` prop renders without
 *                          the modal overlay — route /flight/bookings/:id/details).
 *   - 2026-10-08 Claude — Convenience-fee / Discount lines print the admin-given rule names (fare.convenience_label / fare.discount_label); generic label as fallback.
 *   - 2026-10-06 Claude — Fare: Base fare includes the processing fee; Convenience fee in its place.
 *                          Client decision: the Processing fee (our markup) is not
 *                          a line of its own — it is inside the Base fare, and the
 *                          Convenience fee (shown even at ₹0) takes its place.
 *                          Display only: no total or stored figure changes.
 *   - 2026-10-06 Claude — The change-of-plane line between two sectors is the
 *                          storefront's strip (`.det-layover`): centred, amber,
 *                          "Require to change plane at X" in bold with the
 *                          layover time muted beside it — same wording as the
 *                          customer's Booking Details. It was plain muted text.
 *   - 2026-09-25 Claude — The Fare block reads `b.fare.summary` for the add-on,
 *                              insurance and reschedule lines, so this drawer, the
 *                              e-ticket and the tax invoice state the same figures.
 *                              It also covers two cases the stored blob alone did
 *                              not: post-booking add-ons (`addon_amount`) and an SSR
 *                              the airline dropped at book. Keeps an "Other
 *                              adjustments" row — the CONSOLE shows a row the ledger
 *                              cannot describe; the customer's copy does not. §30.
 *   - 2026-09-23 Claude — Two per-sector defects in the Passengers table.
 *                          (a) "Meal, Baggage, Seat & Other Preference" printed one
 *                              add-on TWICE per sector, on BOTH travellers, whenever
 *                              two passengers chose the same meal on the same flight
 *                              ("GOI-BOM: Baby Meal, Baby Meal"). This drawer never
 *                              read the supplier's own per-traveller `ssrByRoute` —
 *                              which liveFlightBooking.service has been merging on
 *                              since 2026-09-10 and the e-ticket prefers — and went
 *                              straight to `confirmed_addons`, which flattens every
 *                              traveller's SSR into one list with no passenger on it.
 *                              It now reads ssrByRoute first, and the fallback
 *                              attributes via lib/paxSectorDetail's consuming
 *                              pair-up. The "Requested" chip is suppressed where the
 *                              supplier HAS answered per sector, so provisioned SSR
 *                              is no longer labelled as merely requested.
 *                          (b) the ticket number cell printed `ticketNumber` — the
 *                              joined SUMMARY of every number — inside the per-sector
 *                              loop, so a 4-sector booking would show the whole list
 *                              four times. Uses the shared ticketNoOf, as the
 *                              e-ticket and both storefronts now do.
 *   - 2026-09-23 Claude — Takes the screen's `audience` so its document links
 *                          record where they were opened from (lib/docOrigin) —
 *                          a corporate booking's invoice then keeps the
 *                          Corporate sidebar group open instead of the B2C one.
 *   - 2026-09-16 Claude — the Passengers table overflowed the drawer: its fourth
 *                          column ("Meal, Baggage, Seat & Other Preference") was
 *                          drawn past the panel's right edge. It is a `.table`
 *                          but not inside a `.table-wrap`, so the global nowrap
 *                          rule had it size to content with nothing to scroll.
 *                          Marked `.pax-cols` — see admin.css for the wrapping +
 *                          fixed-width layout that keeps all four columns inside
 *                          the modal and aligned.
 *   - 2026-09-16 Claude — "Meal, Baggage, Seat & Other Preference" read "—" on
 *                          every PENDING booking, while the Fare block three
 *                          rows below it billed ₹3,800 of "Meal, Baggage &
 *                          Seat" — the same modal disagreeing with itself about
 *                          whether anything had been bought. The column was
 *                          gated on `confirmed_addons`, which is NULL until
 *                          TripJack confirms the order, and a flight tickets
 *                          asynchronously: a Pending booking is always in that
 *                          state, so the gate was reading "not answered yet" as
 *                          "the airline dropped them". It now falls back to
 *                          `selected_addons` (what the customer picked and was
 *                          charged for, per sector + paxIndex) whenever the
 *                          supplier has not answered, tagged with a "Requested"
 *                          chip. Once `confirmed_addons` IS an array the
 *                          original rule is untouched — a dropped add-on is
 *                          still never listed as delivered.
 *   - 2026-09-11 Claude — Passengers list every per-flight frequent-flyer
 *                          membership via lib/frequentFlyer.
 *   - 2026-09-10 Claude — PARITY with the customer Booking Details page, which
 *                          this drawer had fallen well behind:
 *                          (a) the Fare block had NO add-ons line, so its rows did
 *                              not add up to the Total printed under them — a
 *                              booking with ₹1,870 of meal/baggage/seat listed
 *                              ₹10,514 against a ₹12,384 total. Billed now from the
 *                              stored fareDetail.ancillary, never back-solved;
 *                          (b) Passengers showed Name / Type / Seat / Ticket only.
 *                              It now carries DOB, passport (+nationality, issue,
 *                              expiry) and FREQUENT FLYER, a PNR + ticket number per
 *                              sector with the booking status, and each sector's own
 *                              meal / baggage / seat — gated on confirmed_addons, so
 *                              an add-on the airline dropped is never listed as
 *                              delivered (same rule as the admin e-ticket);
 *                          (c) sectors show the real AIRLINE LOGO (the ported
 *                              AirlineLogo, same asset set as the e-ticket) and a
 *                              LAYOVER strip between the sectors of one leg — a
 *                              10-hour wait and a 40-minute one previously read
 *                              identically;
 *                          (d) "GST" relabelled "Taxes and fees" (same figure —
 *                              it also carries the management fee + its GST).
 *                          (b) and (c) come from what the details endpoint ALREADY
 *                          returned; the per-sector PNR did not, and is the one
 *                          part of this that needed the server. The supplier issues
 *                          a PNR per operated flight and hands back the whole map,
 *                          but only its first entry was ever stored, so a trip
 *                          across two carriers showed one airline's code on the
 *                          other's flights. The map is now persisted at book time
 *                          (booking backend) and served as `pnr_by_route` (admin
 *                          backend); this reads it, falling back to the old
 *                          single-code behaviour for bookings placed before that.
 *   - 2026-08-04 Claude — CR D2: added a "Share by Email" action opening
 *                          ShareEmailModal (Ticket/Invoice/Both, real PDF
 *                          attachment, logged).
 *   - 2026-08-04 Claude — Added the Timeline card (booking_logs operational
 *                          history via components/BookingTimeline).
 *   - 2026-08-04 Claude — Sector middle uses the same directional runway (dashed
 *                          line, centred ✈, arrowhead) as TicketDoc / the customer
 *                          voucher, replacing the bare "✈ 2h 5m" text.
 *   - 2026-08-04 Claude — onUpdate/onCancel are optional (matching the hotel
 *                          drawer): the Transaction Log opens this drawer
 *                          read-only, without the Update/Cancel actions.
 *   - 2026-07-19 Claude — Added the Cart Amendments card (list + Book Amendments)
 *                          opening the RaiseFlightAmendments popup.
 *   - 2026-07-09 Claude — Initial (Booking List row actions).
 *   - 2026-08-10 Claude — Invoice / E-Ticket / Share are gated by docAccess()
 *                          (client-approved rule: none while pending, all when
 *                          confirmed, invoice only when cancelled), the
 *                          Processing fee line always itemises (₹0.00 instead
 *                          of vanishing — QA read the hidden line as missing
 *                          data), and the coupon line is labelled "Discount"
 *                          (it now also carries the auto, non-coupon discount).
 *   - 2026-09-25 Claude — The drawer is a FIXED box that scrolls inside
 *                          (`.lk-modal.lk-tall` + one `.lk-modal-body` wrapping all
 *                          three states) and its loading state is a DrawerSkeleton
 *                          shaped like the body, not a one-line "Loading…". It used
 *                          to open at that line's height and jump when the data
 *                          landed — measured 244px → 1,410px on this drawer — then
 *                          again as the amendments and timeline queries answered
 *                          separately. Now the frame never resizes once the body is
 *                          at the viewport ceiling, so a late query moves nothing.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Icon from '../../components/Icon';
import FullScreenButton from '../../components/FullScreenButton';
import DrawerSkeleton from '../../components/DrawerSkeleton';
import Chip from '../../components/Chip';
import { getBookingDetails, listAmendments } from '../../api/flightBookings';
import { docPath } from '../../lib/docOrigin';
import { docAccess } from './cells';
import BookingTimeline from '../../components/BookingTimeline';
import RefundPipeline from '../../components/RefundPipeline';
import ShareEmailModal from '../../components/ShareEmailModal';
import RaiseFlightAmendmentsModal from './RaiseFlightAmendmentsModal';
import AmendmentActionModal from './AmendmentActionModal';
import AirlineLogo from '../../components/AirlineLogo';
import { ffLabels } from '../../lib/frequentFlyer';
import { ticketNoOf, confirmedAddonLabels, supplierSsrLabels } from '../../lib/paxSectorDetail';

const inr2 = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function dt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const mins = (m) => (m ? `${Math.floor(m / 60)}h ${m % 60}m` : '');

/** "1995-04-02" → "02 Apr 1995"; blank stays blank (never the string "Invalid Date"). */
function dmy(v) {
  const m = String(v || '').match(/^(d{4})-(d{2})-(d{2})/);
  if (!m) return String(v || '');
  const d = new Date(`${m[0]}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(v)
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Ground time between two sectors of one leg, in minutes. 0/negative → no strip. */
const layoverMin = (arrISO, nextDepISO) => {
  const a = new Date(arrISO).getTime();
  const b = new Date(nextDepISO).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, Math.round((b - a) / 60000));
};

/** Every physical sector of a leg — a non-connecting leg synthesises one from its
 *  own fields, the same fallback shape TicketDoc and the customer voucher use. */
const segsOf = (f) => (Array.isArray(f?.segments) && f.segments.length ? f.segments : [{
  fromCode: f?.fromCode, toCode: f?.toCode, fromCity: f?.fromCity, toCity: f?.toCity,
  depISO: f?.depISO, arrISO: f?.arrISO, airlineName: f?.airline, cabin: f?.cabinName,
  airlineCode: String(f?.code || '').toUpperCase().replace(/[^A-Z0-9]/g, '').match(/^[A-Z0-9]{2}/)?.[0] || '',
  flightNo: String(f?.code || '').replace(/^[A-Z0-9]{2}/i, ''), pnr: f?.pnr,
}]);

function Sector({ s }) {
  return (
    <div className="det-sector">
      <div className="det-sector-air">
        {/* The airline's real mark, same component and same asset set the admin
            e-ticket and the customer's voucher use — the drawer was the only
            flight surface still showing the carrier as bare text. */}
        <AirlineLogo code={s.airlineCode} label={s.airlineName || s.airlineCode} size={22} />
        <b>{s.airlineName || s.airlineCode || 'Flight'}</b>
        <span className="muted"> {s.airlineCode}{s.flightNo}</span>
        {s.cabin ? <Chip tone="warn">{s.cabin}</Chip> : null}
        {/* PNR is issued PER OPERATED FLIGHT: a connection through two carriers
            carries two, and the customer's page shows each against its own
            sector. `pnr` here is the sector's own when the record has one,
            falling back to the booking-level code. */}
        {s.pnr ? <span className="muted mono"> · PNR {s.pnr}</span> : null}
      </div>
      <div className="det-sector-route">
        <div>
          <div className="mono"><b>{s.fromCode}</b></div>
          <div className="muted">{s.fromCity}{s.fromTerminal ? ` · T${s.fromTerminal}` : ''}</div>
          <div>{dt(s.depISO)}</div>
        </div>
        <div className="det-arrow">
          {mins(s.durationMin) ? <div>{mins(s.durationMin)}</div> : null}
          {/* Decorative — the codes/times beside it carry the meaning. */}
          <div className="tkt-runway" aria-hidden="true">
            <span className="rw-line" />
            <span className="rw-plane">✈</span>
            <span className="rw-line" />
            <span className="rw-head" />
          </div>
        </div>
        <div>
          <div className="mono"><b>{s.toCode}</b></div>
          <div className="muted">{s.toCity}{s.toTerminal ? ` · T${s.toTerminal}` : ''}</div>
          <div>{dt(s.arrISO)}</div>
        </div>
      </div>
    </div>
  );
}

export default function BookingDetailsDrawer({ bookingId, audience = 'b2c', onClose, onUpdate, onCancel, page = false }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['flight-booking-details', bookingId],
    queryFn: () => getBookingDetails(bookingId),
    enabled: !!bookingId,
  });
  const b = data?.data;
  // Every physical sector of the trip, derived once for the whole table — each
  // carrying its OWN PNR. A PNR is issued per OPERATED FLIGHT, so a connection
  // across two carriers has two of them, and printing the booking-level code
  // against every sector showed one airline's PNR on the other's flights.
  //
  // `pnr_by_route` is the supplier's own map, persisted at book time. A booking
  // placed before that, or one that never ticketed, has none — then this falls
  // back through the stored sector/leg PNR to the booking-level code, which is
  // exactly what this drawer showed before.
  const pnrByRoute = b?.pnr_by_route || {};
  const paxSectors = (b?.legs || []).flatMap((leg) => segsOf(leg).map((sg) => {
    const route = `${sg.fromCode}-${sg.toCode}`;
    return { route, pnr: pnrByRoute[route] || sg.pnr || leg.pnr || b?.pnr_code || '' };
  }));
  // Add-ons the customer paid for, from the STORED fare detail (fareDetail.
  // ancillary, written at book time) — the same source the customer page bills
  // "Meal, Baggage & Seat" from. `mealSeatBaggage` is the combined figure; the
  // per-kind split exists on newer bookings only, so fall back to summing it.
  const anc = b?.fare?.detail?.ancillary || {};
  const num = (v) => Number(v) || 0;
  // Prefer the server-composed fare summary (admin_backend/src/utils/
  // flightFareSummary.js — the mirror of the module the CUSTOMER's copy is built
  // from), so this drawer, the e-ticket, the tax invoice and the storefront all
  // state the same add-on and reschedule figures. It also covers two cases the
  // stored blob alone does not: post-booking add-ons (`addon_amount`), and an SSR
  // the airline dropped at book — which the customer was charged for even though
  // the supplier never received it. See CLAUDE.md §30.
  const svc = b?.fare?.summary || null;
  const ancillaryTotal = svc
    ? num(svc.addons)
    : Math.round((num(anc.mealSeatBaggage)
      || num(anc.meal) + num(anc.seat) + num(anc.baggage)) * 100) / 100;
  const insuranceTotal = svc
    ? num(svc.insurance)
    : Math.round(num(anc.travelInsurance) * 100) / 100;
  // A reschedule grew `total_paid` by its own charge and this block had no line
  // for it, so on a rescheduled booking the rows fell short of the Total beneath
  // them — the same defect the add-on line above was added to fix.
  const reissueTotal = svc ? num(svc.reissue) : 0;
  // Zero on every booking the ledger can describe. The CONSOLE keeps it (the
  // customer's copy does not) so a row that cannot be reconciled is visible to
  // staff rather than silently absorbed into a real line.
  const fareResidual = svc ? num(svc.other) : 0;
  // Has the SUPPLIER answered about the add-ons yet? `confirmed_addons` is an
  // array once TripJack confirms the order and **null while it has not** — a
  // flight tickets asynchronously, so a Pending booking always sits here. An
  // absent array means "not verified yet", NOT "the airline dropped them", and
  // the two must not render identically (they did: both as an em dash).
  const addonsUnverified = !Array.isArray(b?.confirmed_addons);
  const docs = docAccess(b?.booking_status);

  const [raiseOpen, setRaiseOpen] = useState(false);
  const [action, setAction] = useState(null); // { type } for Void / Full Refund
  const [shareOpen, setShareOpen] = useState(false);
  const amendmentsQ = useQuery({
    queryKey: ['flight-booking-amendments', bookingId],
    queryFn: () => listAmendments(bookingId),
    enabled: !!bookingId,
  });
  const amendments = amendmentsQ.data?.data || [];

  return (
    <>
    <div className={page ? 'booking-page' : 'lk-overlay'} onMouseDown={(e) => !page && e.target === e.currentTarget && onClose()}>
      <div className="lk-modal lk-tall panel glass" style={page ? { padding: 24 } : { maxWidth: 760, width: '96%' }}>
        <div className="lk-modal-head">
          <h3>Booking Details {b ? `· #${b.id}` : ''}</h3>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            {!page ? <FullScreenButton product="flight" bookingId={bookingId} audience={audience} /> : null}
            <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
          </span>
        </div>

        <div className="lk-modal-body">
        {isLoading ? <DrawerSkeleton cells={6} blocks={4} />
          : isError || !b ? <div className="empty">Could not load booking.</div>
          : (
          <div className="det-body">
            {/* Summary */}
            <div className="det-grid">
              <div><span className="muted">Booking Ref</span><div><b>{b.booking_reference || '—'}</b></div></div>
              <div><span className="muted">PNR</span><div className="mono">{b.pnr_code || '—'}</div></div>
              <div>
                <span className="muted">Status</span>
                <div><Chip tone={/cancel|fail/i.test(b.booking_status) ? 'rej' : /pend/i.test(b.booking_status) ? 'pend' : 'ok'}>{b.booking_status || '—'}</Chip></div>
              </div>
              <div>
                <span className="muted">Customer</span>
                <div>{b.customer_email || '—'} {b.is_guest ? <Chip tone="warn">Guest</Chip> : null}</div>
              </div>
              <div><span className="muted">Trip</span><div>{b.trip_type}</div></div>
              <div><span className="muted">Booked On</span><div>{dt(b.created_at)}</div></div>
            </div>

            {/* Itinerary */}
            <h4 className="det-h">Itinerary</h4>
            {(b.legs || []).length === 0 ? <div className="muted">No stored itinerary.</div>
              : b.legs.map((leg, i) => (
                <div className="det-leg" key={i}>
                  <div className="det-leg-head">
                    <b>{leg.fromCode || leg.from} → {leg.toCode || leg.to}</b>
                    {leg.pnr ? <span className="muted"> · PNR {leg.pnr}</span> : null}
                  </div>
                  {(() => {
                    const segs = segsOf(leg);
                    return segs.map((s, j) => (
                      <div key={j}>
                        <Sector s={{ ...s, pnr: pnrByRoute[`${s.fromCode}-${s.toCode}`] || s.pnr || leg.pnr || b.pnr_code || '' }} />
                        {/* Ground time between two sectors of ONE leg. A connection
                            was previously drawn as two adjacent sectors with nothing
                            between them, so a 10-hour wait and a 40-minute one read
                            identically — the customer's page separates them with
                            exactly this strip. */}
                        {j < segs.length - 1 && layoverMin(s.arrISO, segs[j + 1].depISO) > 0 ? (
                          <div className="det-layover">
                            <b>Require to change plane at {s.toCity || s.toCode}</b>
                            <span className="det-layover-time">Layover time · {mins(layoverMin(s.arrISO, segs[j + 1].depISO))}</span>
                          </div>
                        ) : null}
                      </div>
                    ));
                  })()}
                  {leg.baggage ? (
                    <div className="muted det-bag">
                      Baggage: {leg.baggage.checkIn || '—'} check-in · {leg.baggage.cabin || '—'} cabin
                    </div>
                  ) : null}
                </div>
              ))}

            {/* Passengers */}
            <h4 className="det-h">Passengers</h4>
            {/* Every physical sector of the trip, so PNR / ticket number /
                preferences read PER FLIGHT the way the customer's own booking
                page lists them. A PNR is issued per operated flight, so a
                connection across two carriers carries two of them — see
                paxSectors for where each one comes from. */}
            {(b.passengers || []).length === 0 && (b.ticketed_pax || []).length === 0
              ? <div className="muted">No passenger records.</div>
              : (
              <table className="table det-pax pax-cols">
                <thead><tr>
                  <th>Sr.</th>
                  <th>Name, DOB, Passport &amp; FF</th>
                  <th>PNR, Ticket No. &amp; Status</th>
                  <th>Meal, Baggage, Seat &amp; Other Preference</th>
                </tr></thead>
                <tbody>
                  {(b.passengers || []).map((p, i) => {
                    const tk = (b.ticketed_pax || [])[i] || {};
                    const name = [p.title, p.firstName, p.lastName].filter(Boolean).join(' ')
                      || p.name || tk.full_name || '—';
                    // The ticket number is resolved PER SECTOR below. `tk` carries
                    // the number an admin corrected by hand (flight_passengers), which
                    // is the last fallback when the supplier issued none of its own.
                    const paxForTicket = { ...p, ticketNumber: p.ticketNumber || tk.ticket_number || null };
                    const seat = p.seat || tk.seat_number || '';
                    // Did the SUPPLIER answer per sector for this traveller? Gates the
                    // "Requested" chip below: `confirmed_addons` can be null on a
                    // pre-capture booking that nonetheless has live ssrByRoute, and
                    // labelling provisioned SSR as merely requested understates it.
                    const hasSupplierSsr = Array.isArray(p.ssrByRoute) && p.ssrByRoute.length > 0;
                    // Everything this traveller holds on THIS sector beyond the bare
                    // ticket. ONCE THE SUPPLIER HAS ANSWERED this is gated on
                    // `confirmed_addons` — what it actually provisioned — never on the
                    // raw selection, so an add-on the airline dropped is not listed as
                    // delivered (same rule as the admin e-ticket's prefOf()). Until
                    // then (addonsUnverified) there is nothing to gate on, and the
                    // selection is shown as "Requested" rather than hidden. The
                    // readable label lives on the selection either way — it is the
                    // only place it is kept.
                    const prefOf = (sec) => {
                      // The supplier's OWN per-traveller answer, when the live read
                      // supplied one. It is built per traveller (ssrByRoute), so it
                      // needs none of the attribution confirmedAddonLabels has to do
                      // and can never double one meal onto two passengers — which is
                      // exactly what this drawer did by ignoring it and going straight
                      // to the flattened confirmed_addons below.
                      const supplied = supplierSsrLabels(p, sec.route);
                      // Seeds the cell rather than returning, so an add-on bought
                      // AFTER ticketing (the amendment loop at the bottom) is still
                      // appended to it.
                      const parts = supplied && supplied.length ? [...supplied] : [];
                      if (parts.length) {
                        // Already answered by the supplier for this traveller.
                      } else if (addonsUnverified) {
                        // The supplier has told us nothing yet (Pending / never
                        // ticketed), so there is nothing to gate on — fall back to
                        // what this traveller SELECTED and was CHARGED for, which
                        // the Fare block on the same screen is already billing.
                        // Flagged "Requested" at the cell, so it is still never
                        // read as delivered.
                        for (const sel of (b.selected_addons || [])) {
                          if (sel.route !== sec.route) continue;
                          if (sel.paxIndex != null && Number(sel.paxIndex) !== i) continue;
                          const label = sel.label || sel.code;
                          if (label) parts.push(/seat/i.test(sel.type) ? `Seat ${label}` : String(label));
                        }
                      } else {
                        // What the airline actually provisioned, attributed to THIS
                        // traveller — see lib/paxSectorDetail for why a filter cannot
                        // do this and a consuming pair-up has to.
                        parts.push(...confirmedAddonLabels({
                          confirmed: b.confirmed_addons,
                          selected: b.selected_addons,
                          route: sec.route,
                          paxIndex: i,
                        }));
                      }
                      // Bought AFTER ticketing, via an amendment.
                      for (const a of (b.addons || [])) {
                        const mine = !a.travellerId || Number(a.travellerId) === i + 1;
                        const here = !a.from || `${a.from}-${a.to}` === sec.route;
                        if (mine && here) parts.push(a.desc || `${a.type} ${a.code}`.trim());
                      }
                      return parts;
                    };
                    return (
                      <tr key={i}>
                        <td className="muted">{i + 1}</td>
                        <td>
                          <b>{name} ({String(p.type || 'ADULT')[0]})</b>
                          {/* Identity, each line only when we hold it — a domestic
                              booking with no passport must not print blank rows. */}
                          <div className="muted det-pax-id">
                            {p.dob ? <div>DOB: {dmy(p.dob)}</div> : null}
                            {p.passportNumber ? (
                              <div>PP: {p.passportNumber}{p.nationality ? ` · N: ${p.nationality}` : ''}</div>
                            ) : null}
                            {(p.passportIssue || p.passportExpiry) ? (
                              <div>ID: {[p.passportIssue, p.passportExpiry].filter(Boolean).map(dmy).join(', ')}</div>
                            ) : null}
                            {ffLabels(p).map((l, k) => <div key={k}>FF: {l}</div>)}
                          </div>
                        </td>
                        <td>
                          {paxSectors.length === 0 ? <span className="muted">—</span> : paxSectors.map((sec, k) => (
                            <div key={k}>
                              <span className="muted">{sec.route}:</span>{' '}
                              <span className="mono"><b>{sec.pnr || '—'}</b></span>
                              {/* THIS sector's ticket number — one is issued per
                                  sector, so printing the joined summary field here
                                  showed the whole list on every row. */}
                              {ticketNoOf(paxForTicket, sec, k)
                                ? <span className="muted"> ({ticketNoOf(paxForTicket, sec, k)})</span>
                                : null}
                            </div>
                          ))}
                          <span className="muted det-pax-id">{b.booking_status || '—'}</span>
                        </td>
                        <td className="muted">
                          {(() => {
                            const rows = paxSectors
                              .map((sec) => [sec.route, prefOf(sec)])
                              .filter(([, parts]) => parts.length);
                            if (rows.length) {
                              return (
                                <>
                                  {rows.map(([route, parts]) => (
                                    <div key={route}><span className="muted">{route}:</span> {parts.join(', ')}</div>
                                  ))}
                                  {/* Paid for, not yet confirmed BY THE AIRLINE.
                                      That distinction is the whole point of the
                                      gate, so the cell says which of the two it
                                      is showing rather than implying delivery. */}
                                  {addonsUnverified && !hasSupplierSsr ? (
                                    <div className="det-pax-id"><Chip tone="pend">Requested</Chip></div>
                                  ) : null}
                                </>
                              );
                            }
                            // Nothing provisioned per sector — a seat the airline
                            // assigned with no matching SSR row still belongs here.
                            return seat ? <span>Seat {seat}</span> : <span>—</span>;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                  {(b.passengers || []).length === 0 && (b.ticketed_pax || []).map((tk, i) => (
                    <tr key={`t${i}`}>
                      <td className="muted">{i + 1}</td>
                      <td><b>{tk.full_name || '—'}</b></td>
                      <td className="mono">{tk.ticket_number || '—'}</td>
                      <td className="muted">{tk.seat_number ? `Seat ${tk.seat_number}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Fare */}
            <h4 className="det-h">Fare</h4>
            <div className="det-fare">
              {/* Base fare INCLUDES our processing fee (no line of its own). */}
              <Row k="Base fare" v={inr2(num(b.fare.base_fare) + num(b.fare.processing_fee))} />
              {/* "GST" understated what this line is: it carries the airline
                  taxes AND the supplier management fee + its GST, which is why
                  the customer's own page calls it Taxes and fees. Same figure. */}
              <Row k="Taxes and fees" v={inr2(b.fare.gst_amount)} />
              <Row k={b.fare.convenience_label || 'Convenience fee'} v={inr2(b.fare.convenience_fee)} />
              {/* Meal / seat / extra baggage. This line did not exist, so the
                  printed rows did not add up to the Total beneath them — on the
                  reported booking they came to ₹10,514 against a ₹12,384 total,
                  short by exactly the ₹1,870 of add-ons the customer had paid
                  for. Taken from the stored fare detail, never back-solved from
                  the total, so it can never invent a charge that was not made. */}
              {ancillaryTotal > 0 ? (
                <Row k="Meal, Baggage & Seat" v={inr2(ancillaryTotal)} />
              ) : null}
              {insuranceTotal > 0 ? (
                <Row k="Travel insurance" v={inr2(insuranceTotal)} />
              ) : null}
              {b.fare.transaction_fee ? <Row k="Transaction fee" v={inr2(b.fare.transaction_fee)} /> : null}
              {b.fare.coupon_discount ? <Row k={b.fare.discount_label || `Discount${b.fare.coupon_code ? ` (${b.fare.coupon_code})` : ''}`} v={`− ${inr2(b.fare.coupon_discount)}`} /> : null}
              {reissueTotal > 0 ? <Row k="Reschedule charge" v={inr2(reissueTotal)} /> : null}
              {Math.abs(fareResidual) >= 0.01 ? <Row k="Other adjustments" v={inr2(fareResidual)} /> : null}
              <Row k="Total paid" v={inr2(b.fare.total_paid)} strong />
              {/* How that total was actually settled. A customer can pay part (or
                  all) of a booking from their Neon Wallet, and a refund has to
                  know which instrument to return the money to. Shown only when a
                  wallet was involved — a card-only booking reads as before. */}
              {b.fare.wallet_paid > 0 ? (
                <>
                  <Row k="↳ from wallet" v={inr2(b.fare.wallet_paid)} />
                  <Row k="↳ from gateway" v={inr2(b.fare.gateway_paid)} />
                </>
              ) : null}
            </div>

            {/* Refund workflow (CR E1) — stepper + history + admin transitions. */}
            <RefundPipeline
              kind="flight"
              bookingId={b.id}
              refund={b.refund}
              bookingStatus={b.booking_status}
              detailsQueryKey={['flight-booking-details', bookingId]}
            />

            {/* Cart Amendments */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 16 }}>
              <h4 className="det-h" style={{ margin: 0 }}>Cart Amendments</h4>
              <button className="btn ghost" onClick={() => setRaiseOpen(true)}>
                <Icon name="plus" /> Book Amendments
              </button>
            </div>
            {amendmentsQ.isLoading ? <div className="muted">Loading amendments…</div>
              : amendments.length === 0 ? <div className="muted">No amendments raised on this booking.</div>
              : (
              <table className="table det-pax">
                <thead><tr><th>Type</th><th>Status</th><th>Raised by</th><th>On</th></tr></thead>
                <tbody>
                  {amendments.map((a) => (
                    <tr key={a.amendment_id}>
                      <td>{prettyType(a.type)}</td>
                      <td><Chip tone={/reject|fail/i.test(a.status) ? 'rej' : /success/i.test(a.status) ? 'ok' : 'pend'}>{a.status}</Chip></td>
                      <td>{a.created_by || '—'}</td>
                      <td>{dt(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Timeline */}
            <BookingTimeline kind="flight" bookingId={b.id} />

            {/* Actions — which documents exist is the docAccess() rule: nothing
                while pending, everything when confirmed, invoice only when
                cancelled. */}
            <div className="form-actions" style={{ flexWrap: 'wrap', gap: 8 }}>
              {docs.invoice ? (
                <Link className="btn ghost" to={docPath(`/flight/bookings/${b.id}/invoice`, audience)} onClick={onClose}>
                  <Icon name="download" /> Tax Invoice
                </Link>
              ) : null}
              {docs.voucher ? (
                <Link className="btn ghost" to={docPath(`/flight/bookings/${b.id}/ticket`, audience)} onClick={onClose}>
                  <Icon name="page" /> E-Ticket
                </Link>
              ) : null}
              {docs.share ? (
                <button className="btn ghost" onClick={() => setShareOpen(true)}>
                  <Icon name="mail" /> Share by Email
                </button>
              ) : null}
              {onUpdate ? (
                <button className="btn ghost" onClick={() => onUpdate(b)}><Icon name="edit" /> Update</button>
              ) : null}
              {onCancel && !/cancel|refund/i.test(b.booking_status) ? (
                <button className="btn danger" onClick={() => onCancel(b)}><Icon name="x" /> Cancel booking</button>
              ) : null}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>

    {raiseOpen && b && (
      <RaiseFlightAmendmentsModal
        booking={b}
        onClose={() => setRaiseOpen(false)}
        onCancellation={(bk) => onCancel?.(bk)}
        onAction={(type) => setAction({ type })}
        onRaised={() => amendmentsQ.refetch()}
      />
    )}

    {action && b && (
      <AmendmentActionModal
        booking={b}
        type={action.type}
        onClose={() => { setAction(null); amendmentsQ.refetch(); }}
      />
    )}

    {shareOpen && b && (
      <ShareEmailModal kind="flight" booking={b} onClose={() => setShareOpen(false)} />
    )}
    </>
  );
}

// Turn a stored amendment type (SSR / FARE_CHANGE / …) into a readable label.
function prettyType(t) {
  if (!t) return '—';
  if (/^SSR$/i.test(t)) return 'Special Service Request';
  return String(t)
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Row({ k, v, strong }) {
  return (
    <div className="det-fare-row" style={strong ? { fontWeight: 700, borderTop: '1px solid var(--line, #e5e7eb)', paddingTop: 6 } : undefined}>
      <span>{k}</span><span>{v}</span>
    </div>
  );
}
